import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FreightInvoiceSummary } from './freight-invoice-summary.model';
import { SendToIntacctInvoice } from '../../components/common/send-to-intacct/send-to-intacct-invoice.model';
import { FreightInvoice } from './freight-invoice.model';
import { RailcarLease } from '../railcar/railcar-lease.model';
import { FreightARInvoiceSummary } from './freight-invoice-ar-summary.model';

@Injectable({providedIn: 'root'})
export class FreightService {

	private url: string;

	constructor(private httpClient: HttpClient) {
		this.url = 'api/freight/';
	}

	public getAsync(id: number): Observable<FreightInvoice> {
		return this.httpClient.get<FreightInvoice>(this.url + id);
	}

	public searchFreightInvoices(tabValue: string, dateForClosedInvoices: Date, dateForDisputedInvoices: Date): Observable<FreightInvoiceSummary[]> {
		const date = tabValue === 'Closed' ? dateForClosedInvoices : tabValue === 'Disputed' ? dateForDisputedInvoices : undefined;
		if (date) {
			return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'searchFreightInvoices' +
				'?closedInvoicesMonth=' + (date.getMonth() + 1) +
				'&closedInvoicesYear=' + date.getFullYear() +
				'&isDispute=' + !!(tabValue === 'Disputed'));
		} else {
			return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'searchFreightInvoices');
		}
	}

	public getByInvoiceNumber(invoiceNumber: string): Observable<FreightInvoiceSummary[]> {
		return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'getByInvoiceNumber?invoiceNumber=' + invoiceNumber);
	}

	public findInvoiceNumberAvailability(invoiceNumber: string, facilityId: number, freightInvoiceId: number): Observable<FreightInvoiceSummary[]> {
		if (freightInvoiceId) {
			return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'findInvoiceNumberAvailability?invoiceNumber=' + invoiceNumber
				+ '&facilityId=' + facilityId + '&freightInvoiceId=' + freightInvoiceId);
		}
		return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'findInvoiceNumberAvailability?invoiceNumber=' + invoiceNumber
			+ '&facilityId=' + facilityId);
	}

	public getByRailcar(railcarId: number): Observable<FreightInvoiceSummary[]> {
		return this.httpClient.get<FreightInvoiceSummary[]>(this.url + 'getByRailcar?railcarId=' + railcarId);
	}

	public getLastNLeases(railcarId: number, n: number): Observable<RailcarLease[]> {
		return this.httpClient.get<RailcarLease[]>(this.url + 'getLastNLeases?railcarId=' + railcarId + '&n=' + n);
	}

	public reverseAPFreightAccountsPayable(invoiceId: number): Observable<string[]> {
		return this.httpClient.post<string[]>(this.url + 'reverseAccountsPayable', invoiceId);
	}

	public searchArInvoices(billingPeriod: Date): Observable<any> {
		return this.httpClient.get<FreightARInvoiceSummary[]>(this.url + '/getFreightArInvoices?billingperiod=' + billingPeriod.toLocaleDateString());
	}

	public getArInvoiceDetails(rebillingId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getFreightArInvoiceDetails?rebillingId=' + rebillingId);
	}

	public getApAttachmentMetaData(rebillingId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getApAttachmentMetaData?invoiceId=' + rebillingId);
	}

	public sendAPFreightAccountsPayables(invoices: SendToIntacctInvoice[]): Observable<any> {
		return this.httpClient.post<any>(this.url + 'sendAccountsPayables',
			invoices.map(x => { return {item1: x.id, item2: x.glPostingDate, item3: x.accountingDueDate}}));
	}

	public save(freightInvoice: FreightInvoice): Observable<FreightInvoice> {
		return this.httpClient.post<FreightInvoice>(this.url, freightInvoice);
	}

	public sendReadyForReview(invoiceIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + 'sendReadyForReview', invoiceIds);
	}

	public sendToApproved(invoiceIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + 'sendToApproved', invoiceIds);
	}

	public acceptPayable(invoiceId: number): Observable<any> {
		return this.httpClient.put<any>(this.url + 'acceptPayableForInvoice?invoiceId=' + invoiceId, null);
	}

	public setInvoiceWorkingStatus(id: number, isWorked: boolean): Observable<any> {
		return this.httpClient.put(this.url + '/setInvoiceWorkingStatus?id=' + id + '&worked=' + isWorked, null);
	}

	public getFreightSwitchArInvoicePdf(id: number) : Observable<string> {
		return this.httpClient.get(this.url + '/getFreightSwitchArInvoicePdf?arId=' + id, { responseType: 'text' });
	}
	public sendInvoices(invoices: any, billingPeriod: Date): Observable<any> {
		return this.httpClient.post(this.url + '/sendInvoices?billingPeriod=' + billingPeriod.toLocaleDateString(), invoices);
	}

	public searchSwitchArInvoicesByType(type: string,id: number, billingPeriodStart: Date, billingPeriodEnd: Date): Observable<FreightARInvoiceSummary[]> {
		return this.httpClient.get<FreightARInvoiceSummary[]>(this.url + '/searchSwitchArInvoicesByType?id=' + id.toString() + '&type=' + type.toString()
			+ '&billingPeriodStart=' + billingPeriodStart.toLocaleDateString()
			+ '&billingPeriodEnd=' + billingPeriodEnd.toLocaleDateString());
	}
	
}
